import {Component, OnInit} from '@angular/core';
import {CustomerService} from './customer.service';
import {Customer} from './customer.model';
import {MenuItem, MessageService} from 'primeng/api';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {formatDate, CommonModule} from '@angular/common';
import {SessionStorageService} from "../services/session-storage.service";
import {Role} from "./customer-role.model";
import {PaymentService} from "../services/payment.service";
import {PersonalPaymentMethod} from "../models/payments/personal-payment-method.model";
import {Company} from "../company/company.model";

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { ProgressBarModule } from "primeng/progressbar";
import { CheckboxModule } from "primeng/checkbox";
import { TabsModule } from 'primeng/tabs';
import { ImageModule } from 'primeng/image';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { CustomerFormComponent } from './customer-form/customer-form.component';
import { FirebaseStorageService } from '../services/firebase-storage.service';
import { EMPTY, finalize, switchMap } from 'rxjs';
import { ToolbarService } from '../services/toolbar.service';


@Component({
    selector: 'app-customer',
    templateUrl: './customer.component.html',
    styleUrls: ['./customer.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ToastModule,
    ButtonModule,
    TableModule,
    ToolbarModule,
    BreadcrumbModule,
    InputTextModule,
    CardModule,
    TagModule,
    ChipModule,
    TabsModule,
    ImageModule,
    ProgressBarModule,
    CheckboxModule,
    TooltipModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    CustomerFormComponent
]
})
export class CustomerComponent implements OnInit {
    private readonly tabKeys = ['details', 'roles', 'payment-methods'] as const;

    customer!: Customer;
    isLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    roles: Role[] = [];
    personalPaymentMethods: PersonalPaymentMethod[] = [];
    companySubLocality: string = '';
    companyLocality: string = '';
    showCustomerForm = false;
    customerFormHeader = 'Edit Customer';
    customerFormSubmitLabel = 'Update';
    isProfileImageUploadInProgress = false;
    profileImageUploadProgress = 0;

    constructor(
        private customerService: CustomerService,
        private messageService: MessageService,
        private paymentService: PaymentService,
        private firebaseStorageService: FirebaseStorageService,
        private sessionStorageService: SessionStorageService,
        private toolbarService: ToolbarService,
        private route: ActivatedRoute, private router: Router
    ) {
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.breadcrumbItems = [
            {label: 'Customer'}
        ];
        this.homeBreadcrumb = {icon: 'pi pi-home', routerLink: '/'};
        this.route.queryParamMap.subscribe(params => {
            this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
        });

        const routeId = this.route.snapshot.paramMap.get('id');
        if (routeId) {
            const numericId = Number(routeId);
            if (Number.isInteger(numericId) && routeId.trim() !== '') {
                this.loadCustomerById(numericId);
                return;
            }
            this.loadCustomer(routeId);
        } else {
            const firebaseId = this.sessionStorageService.getCustomer()!.firebaseId;
            if (firebaseId) {
                this.loadCustomer(firebaseId);
            }
        }
    }

    loadCustomer(customerId: string) {
        this.customerService.getCustomerByFirebaseId(customerId).subscribe(customerWrapper => {
            console.log(customerWrapper);
            this.customer = customerWrapper.data;
            this.customerService.getRolesByCustomerId(this.customer.id).subscribe(rolesData => {
                console.log(rolesData);
                this.roles = rolesData;
                this.isLoading = false;
            });
            this.paymentService.getAllPersonalPaymentMethods(this.customer.firebaseId).subscribe(personalPaymentMethodsData => {
                console.log(personalPaymentMethodsData);
                this.personalPaymentMethods = personalPaymentMethodsData;
                this.isLoading = false;
            });
        });
    }

    loadCustomerById(customerId: number) {
        this.customerService.getCustomerById(customerId).subscribe(customerWrapper => {
            console.log(customerWrapper);
            this.customer = customerWrapper.data;
            this.customerService.getRolesByCustomerId(this.customer.id).subscribe(rolesData => {
                console.log(rolesData);
                this.roles = rolesData;
                this.isLoading = false;
            });
            this.paymentService.getAllPersonalPaymentMethods(this.customer.firebaseId).subscribe(personalPaymentMethodsData => {
                console.log(personalPaymentMethodsData);
                this.personalPaymentMethods = personalPaymentMethodsData;
                this.isLoading = false;
            });
        });
    }

    openEditProfile(): void {
        this.customerFormHeader = 'Edit Customer';
        this.customerFormSubmitLabel = 'Update';
        this.showCustomerForm = true;
    }

    onCustomerFormClosed(): void {
        this.showCustomerForm = false;
        if (this.customer?.firebaseId) {
            this.loadCustomer(this.customer.firebaseId);
        }
    }

    showDate(date: Date | undefined): string {
        if (date) {
            return formatDate(date, 'dd/MM/yyyy', 'en-US');
        }
        return '';
    }

    editRole(role: Role) {
        // Logic to handle role editing
        console.log('Edit role:', role);
    }

    deleteRole(role: Role) {
        // Logic to handle role deletion
        console.log('Delete role:', role);
    }

    createNewPaymentMethod() {
        // Logic to handle creating a new payment method (e.g., open a dialog)
        console.log('Create new payment method');
    }

    editPaymentMethod(paymentMethod: PersonalPaymentMethod) {
        // Logic to handle editing the payment method
        console.log('Edit payment method:', paymentMethod);
    }

    deletePaymentMethod(paymentMethod: PersonalPaymentMethod) {
        // Logic to handle deleting the payment method
        console.log('Delete payment method:', paymentMethod);
    }

    sendEmail(email: string) {
        if (email) {
            window.location.href = `mailto:${email}`;
        }
    }

    makePhoneCall(phoneNumber: string) {
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        }
    }

    uploadProfileImage(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';

        if (!file || !this.customer?.id) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid File', detail: 'Please select an image file for the customer profile.' });
            return;
        }

        const maxImageSizeInBytes = 2 * 1024 * 1024;
        if (file.size > maxImageSizeInBytes) {
            this.messageService.add({ severity: 'warn', summary: 'File Too Large', detail: 'Customer profile image must be 2 MB or smaller.' });
            return;
        }

        const extension = this.getImageFileExtension(file);
        const filePath = `customers/${this.customer.id}/profile/customer-profile.${extension}`;
        this.isProfileImageUploadInProgress = true;
        this.profileImageUploadProgress = 0;

        this.firebaseStorageService.uploadFileToExactPathWithProgress(file, filePath).pipe(
            switchMap(progress => {
                this.profileImageUploadProgress = progress.progress;
                if (!progress.url) {
                    return EMPTY;
                }
                const updatedCustomer = { ...this.customer, imageUrl: `${progress.url}${progress.url.includes('?') ? '&' : '?'}v=${Date.now()}` };
                return this.customerService.updateCustomer(updatedCustomer);
            }),
            finalize(() => this.isProfileImageUploadInProgress = false)
        ).subscribe({
            next: response => {
                if (!response?.data) {
                    return;
                }
                this.customer = response.data;
                this.syncCurrentCustomerState(response.data);
                this.messageService.add({ severity: 'success', summary: 'Profile Updated', detail: 'Customer profile image has been uploaded and saved.' });
            },
            error: error => {
                console.error('Customer profile image upload failed:', error);
                this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload and save the customer profile image.' });
            }
        });
    }

    activeTabIndex: number = 0; // Added activeTabIndex

    onTabChange(event: any) { // Added onTabChange method
        this.activeTabIndex = event.index;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Roles', value: this.roles.length, hint: 'Assigned access', tone: 'info' },
            { label: 'Payment Methods', value: this.personalPaymentMethods.length, hint: 'Stored methods', tone: 'success' },
            { label: 'Phone', value: this.customer?.isPhoneVerified ? 'Verified' : 'Unverified', hint: this.customer?.phoneNumber || 'No phone', tone: this.customer?.isPhoneVerified ? 'success' : 'warn' },
            { label: 'Company', value: this.customer?.company?.name || '-', hint: 'Linked organization', tone: 'info' }
        ];
    }

    get linkedCompany(): Company | null {
        return this.customer?.company ?? null;
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }

    private getImageFileExtension(file: File): string {
        const extensionFromName = file.name.split('.').pop()?.toLowerCase();
        if (extensionFromName && ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(extensionFromName)) {
            return extensionFromName;
        }
        if (file.type === 'image/jpeg') return 'jpg';
        if (file.type === 'image/png') return 'png';
        if (file.type === 'image/webp') return 'webp';
        if (file.type === 'image/gif') return 'gif';
        if (file.type === 'image/svg+xml') return 'svg';
        return 'png';
    }

    private syncCurrentCustomerState(customer: Customer): void {
        const currentCustomer = this.sessionStorageService.getCustomer();
        if (!currentCustomer || (currentCustomer.id !== customer.id && currentCustomer.firebaseId !== customer.firebaseId)) {
            return;
        }

        this.sessionStorageService.saveCustomer(customer);
        this.toolbarService.updateCustomer(customer);
    }
}
